/**
 * Ventas › Envíos: los pedidos físicos y cómo llegan.
 *
 * Dos pestañas. «Pedidos» es la lista de lo que hay que mandar —la misma tabla
 * de Cobros, `OrdersTable`, pero sólo con lo que lleva envío—: en Cobros se
 * mezclaba con descargas, servicios y suscripciones, y lo pendiente de enviar
 * se perdía entre ellos. Se abre con «Por enviar» encendido si hay algo.
 *
 * «Ajustes» son tres cosas y nada más, que es lo que pide Stripe al cobrar uno: las formas de
 * envío que se ofrecen (`ShippingRatesEditor`, la misma pieza del asistente), a
 * qué países se envía, y si un reembolso total devuelve la unidad a las
 * existencias. Todo es del proyecto y vale para todos sus productos.
 */
import { useEffect, useMemo, useState } from 'react'
import { IconMapPin, IconPackage, IconPlus, IconRefresh, IconSettings, IconTruckDelivery, IconX } from '@tabler/icons-react'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { SectionRefresh } from '../../SectionRefresh'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { HeaderRefreshButton } from '../ListSelection'
import { useSectionResource } from '../../../hooks/useSectionResource'
import { useTabThumb } from '../../../hooks/useTabThumb'
import { listOrders } from '../../../lib/api'
import { awaitsShipping, failureDetail } from './offerShared'
import { OrdersTable } from './OrdersTable'
import { ToggleRow } from '../../SettingsControls'
import { getShipping, saveShippingSettings, type ShippingConfig } from '../../../lib/api'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { countryLabel } from '../../../lib/displayNames'
import { useToast } from '../../Toast'
import { ShippingRatesEditor } from './ShippingRatesEditor'

/**
 * Los países que se pueden elegir. Stripe admite casi todos; éstos son los que
 * de verdad se eligen —Norteamérica, Latinoamérica y Europa, que es donde está
 * quien usa esto—, con el nombre en el idioma del panel (`Intl`).
 */
export const SHIPPING_COUNTRIES = [
  'US', 'CA', 'MX', 'GT', 'SV', 'HN', 'NI', 'CR', 'PA', 'DO', 'PR', 'CO', 'VE', 'EC', 'PE', 'BO', 'CL', 'AR', 'UY', 'PY', 'BR',
  'ES', 'PT', 'FR', 'DE', 'IT', 'NL', 'BE', 'LU', 'IE', 'GB', 'CH', 'AT', 'DK', 'SE', 'NO', 'FI', 'PL', 'CZ', 'GR',
  'AU', 'NZ', 'JP', 'KR', 'SG', 'HK', 'IL', 'AE',
]

export function ShippingSection({ projectId, token, canSell = true, onOpenPlans, onOpenOffer, onOpenContact }: {
  projectId: string
  token: string
  canSell?: boolean
  onOpenPlans?: (() => void) | undefined
  onOpenOffer?: ((offerId: string) => void) | undefined
  onOpenContact?: ((identity: string) => void) | undefined
}) {
  const { t, language } = useI18n()
  const [tab, setTab] = useState<'orders' | 'settings'>('orders')
  const [tabsEl, setTabsEl] = useState<HTMLDivElement | null>(null)
  useTabThumb(tabsEl, tab)
  /* Los mismos cobros que Cobros —misma clave, misma caché—, sólo los que
     llevan envío. */
  const ordersResult = useSectionResource(token, [projectId, 'orders'], () => listOrders(projectId, token))
  const shippable = useMemo(() => (ordersResult.data ?? []).filter((order) => order.fulfillmentStatus), [ordersResult.data])
  const toShipCount = useMemo(() => shippable.filter(awaitsShipping).length, [shippable])
  const ordersLoader = useRecordLoader(ordersResult.loading)
  const { showToast } = useToast()
  const [config, setConfig] = useState<ShippingConfig | null>(null)
  const [failed, setFailed] = useState('')

  const load = () => {
    setFailed('')
    void getShipping(projectId, token)
      .then(setConfig)
      .catch((cause) => setFailed(translateServerError(cause, t('error.unexpected'))))
  }
  useEffect(load, [projectId, token]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async (patch: Partial<Pick<ShippingConfig, 'countries' | 'restockOnRefund'>>) => {
    if (!config) return
    const before = config
    setConfig({ ...config, ...patch })
    try {
      const saved = await saveShippingSettings(projectId, patch, token)
      setConfig((current) => (current ? { ...current, ...saved } : current))
    } catch (cause) {
      setConfig(before)
      showToast(translateServerError(cause, t('error.unexpected')), 'error')
    }
  }

  const name = (code: string) => countryLabel(code, language, code)
  const available = config ? SHIPPING_COUNTRIES.filter((code) => !config.countries.includes(code)).sort((a, b) => name(a).localeCompare(name(b), language)) : []

  /* La espera sustituye a la sección entera, como en Cobros. */
  if (ordersLoader.visible) return <SectionLoader loader={ordersLoader} label={t('orders.loading')} />

  return (
    <div className="dash-projects-view dash-pages-view dash-orders-list-view dash-shipping-view dash-loaded-content-in">
      <SectionRefresh refreshing={ordersResult.refreshing} failed={Boolean(ordersResult.error)} />
      <DashboardPageHeader
        title={t('shipping.title')}
        subtitle={t('shipping.subtitle')}
        actions={<HeaderRefreshButton refreshing={ordersResult.refreshing} onRefresh={() => { void ordersResult.refresh(); load() }} />}
      />
      <div
        className="dash-booking-service-tabs dash-shipping-tabs"
        ref={setTabsEl}
        role="tablist"
        aria-label={t('shipping.title')}
      >
        <span className="dash-booking-service-tabs-thumb" aria-hidden="true" />
        <button type="button" role="tab" aria-selected={tab === 'orders'} className={tab === 'orders' ? 'is-active' : ''} onClick={() => setTab('orders')}>
          <IconPackage size={14} aria-hidden="true" />
          <span>{t('shipping.tab.orders')}</span>
          {toShipCount > 0 ? <span className="dash-shipping-tab-count">{toShipCount}</span> : null}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'settings'} className={tab === 'settings' ? 'is-active' : ''} onClick={() => setTab('settings')}>
          <IconSettings size={14} aria-hidden="true" />
          <span>{t('shipping.tab.settings')}</span>
        </button>
      </div>

      {tab === 'orders' ? (
        <OrdersTable
          projectId={projectId}
          token={token}
          orders={shippable}
          failed={Boolean(ordersResult.error) && ordersResult.data === undefined}
          failureReason={ordersResult.error ? failureDetail(ordersResult.error) : ''}
          onReload={() => { void ordersResult.refresh() }}
          canSell={canSell}
          onOpenPlans={onOpenPlans}
          onOpenOffer={onOpenOffer}
          onOpenContact={onOpenContact}
          defaultToShip
          empty={{ title: t('shipping.orders.emptyTitle'), body: t('shipping.orders.emptyBody') }}
        />
      ) : (
        <div className="dash-settings-view dash-shipping-settings">
          {failed ? (
            <div className="dash-placeholder">
              <strong>{t('shipping.loadFailed')}</strong>
              <span>{failed}</span>
              <button type="button" className="dash-secondary-btn" onClick={load}>{t('region.retry')}</button>
            </div>
          ) : !config ? (
            <div className="dash-placeholder"><span>{t('shipping.loading')}</span></div>
          ) : (
            <div className="dash-account-grid">
              <section className="dash-settings-card is-raised dash-shipping-rates-card">
                <div className="dash-settings-card-head">
                  <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconTruckDelivery size={18} /></span>
                  <div className="dash-settings-card-head-copy">
                    <h3>{t('shipping.rates.title')}</h3>
                    <p>{t('shipping.rates.note')}</p>
                  </div>
                </div>
                <ShippingRatesEditor
                  projectId={projectId}
                  token={token}
                  rates={config.rates}
                  maxRates={config.maxRates}
                  onChange={(rates) => setConfig((current) => (current ? { ...current, rates } : current))}
                />
              </section>

              <section className="dash-settings-card is-raised">
                <div className="dash-settings-card-head">
                  <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconMapPin size={18} /></span>
                  <div className="dash-settings-card-head-copy">
                    <h3>{t('shipping.countries.title')}</h3>
                    <p>{t('shipping.countries.note')}</p>
                  </div>
                </div>
                <div className="dash-ship-countries">
                  {config.countries.map((code) => (
                    <span key={code} className="dash-ship-country">
                      {name(code)}
                      {/* El último no se quita: sin ningún país no se podría pedir la dirección. */}
                      {config.countries.length > 1 ? (
                        <button type="button" aria-label={t('shipping.countries.removeAria', { name: name(code) })} onClick={() => { void saveSettings({ countries: config.countries.filter((row) => row !== code) }) }}>
                          <IconX size={12} />
                        </button>
                      ) : null}
                    </span>
                  ))}
                  {available.length ? (
                    <label className="dash-ship-country is-add">
                      <IconPlus size={13} aria-hidden="true" />
                      {t('shipping.countries.add')}
                      <select value="" aria-label={t('shipping.countries.add')} onChange={(event) => { if (event.target.value) void saveSettings({ countries: [...config.countries, event.target.value] }) }}>
                        <option value="" disabled>{t('shipping.countries.add')}</option>
                        {available.map((code) => <option key={code} value={code}>{name(code)}</option>)}
                      </select>
                    </label>
                  ) : null}
                </div>
              </section>

              <section className="dash-settings-card is-raised">
                <div className="dash-settings-card-head">
                  <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconPackage size={18} /></span>
                  <div className="dash-settings-card-head-copy">
                    <h3>{t('shipping.stock.title')}</h3>
                    <p>{t('shipping.stock.note')}</p>
                  </div>
                </div>
                <ToggleRow
                  icon={<IconRefresh size={16} />}
                  checked={config.restockOnRefund}
                  onChange={(value) => { void saveSettings({ restockOnRefund: value }) }}
                  title={t('shipping.stock.restock')}
                  description={t('shipping.stock.restockHint')}
                />
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
